import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useQueryClient } from '@tanstack/react-query';
import ClassroomSettings from '../../storeTabs/classroom/ClassroomSettings';

const BRAND = '#0F7F85';

// 강의실 카드 그리드는 화면(뷰포트) 폭 기준 열 수라, 좁은 모달 안에서는 카드가 너무 좁아져
// 배지('개인 기본'·유형)에 밀린 강의실 이름이 한 글자씩 세로로 꺾인다. 이 모달 안에서만 보정한다.
// - 넓은 화면에서도 최대 3열
// - 이름 줄이 모자라면 배지를 다음 줄로 내리고 이름은 최소 폭을 지킨다
const modalCardFitSx = {
  '& .MuiGrid-container > .MuiGrid-item': {
    '@media (min-width: 1200px)': { flexBasis: '33.3333%', maxWidth: '33.3333%' },
  },
  '& .MuiGrid-item > div > div:first-of-type': { flexWrap: 'wrap' },
  '& .MuiGrid-item > div > div:first-of-type > p:first-of-type': { minWidth: '120px' },
};

// 스케줄 화면의 강의실 목록(search-options/branch-rooms) → 센터설정 강의실 카드가 쓰는 모양(centers 상세의 branchExerciseRoomInfoList)
const toClassroomRoom = room => ({
  roomId: room.branchExerciseRoomId,
  name: room.branchExerciseRoomName,
  roomType: room.roomType,
  maxSession: room.maxSession,
  defaultForPrivateYn: room.defaultForPrivateYn,
  defaultForGroupYn: room.defaultForGroupYn,
  defaultForConsultationYn: room.defaultForConsultationYn,
});

/**
 * 센터설정 > 수업설정 > 강의실 설정 화면(ClassroomSettings)을 지점 스케줄 설정에서 모달로 연다.
 * 추가·수정·삭제 후에는 스케줄 화면의 강의실 목록(['rooms', branchId])을 다시 받는다.
 */
const ClassroomManageModal = ({ open, onClose, branchId, rooms = [] }) => {
  const queryClient = useQueryClient();
  const settingsRef = useRef(null);
  const [roomListInfo, setRoomListInfo] = useState([]);

  useEffect(() => {
    setRoomListInfo(rooms.map(toClassroomRoom));
  }, [rooms]);

  const refreshRooms = () => queryClient.invalidateQueries({ queryKey: ['rooms', branchId] });

  // 삭제는 목록 state 만 고치고 끝나므로 스케줄 쪽 목록도 같이 갱신한다
  const handleSetRoomListInfo = next => {
    setRoomListInfo(next);
    refreshRooms();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      scroll="paper"
      PaperProps={{ sx: { width: '1000px', maxWidth: 'calc(100vw - 32px)', borderRadius: '12px' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', p: '16px 16px 12px 20px' }}>
        <Typography sx={{ flex: 1, fontSize: '16px', fontWeight: 700, color: '#1F2A37' }}>강의실 관리</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => settingsRef.current?.submitForm()}
          sx={{ height: '32px', px: 2, color: BRAND, border: `1px solid ${BRAND}`, borderRadius: '8px' }}>
          강의실 추가
        </Button>
        <IconButton size="small" aria-label="닫기" onClick={onClose} sx={{ color: '#8A929A' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ px: '20px', pb: '20px', overflowY: 'auto', ...modalCardFitSx }}>
        <ClassroomSettings
          ref={settingsRef}
          roomListInfo={roomListInfo}
          setRoomListInfo={handleSetRoomListInfo}
          branchId={branchId}
          getDetailCenter={refreshRooms}
        />
      </Box>
    </Dialog>
  );
};

export default ClassroomManageModal;
